import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getTutorLessonResume,
  listLessons,
  type LessonItem,
  type TutorLessonProgressDTO,
} from "../../modules/tutor/api/tutor.client";
import { useTutorProgressBatch } from "../../modules/tutor/hooks/useTutorProgressBatch";
import { useStudentMe } from "../../modules/roster/hooks/useStudentMe";
import { ROUTES } from "../../utils/constants";
import { Button } from "../ui/Button";
import { ChevronDown, ChevronRight, Loader2, Play } from "lucide-react";
import { useFeatureAccess } from "../../contexts/FeatureAccessContext";
import { WordOfDayHeroPanel } from "../vocabulary/WordOfDayHeroPanel";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import { useTutorTenantLabels } from "../../modules/tutor/hooks/useTutorTenantLabels";
import { useLearnerCurriculum } from "../../modules/curriculum/hooks/useLearnerCurriculum";
import { isEffectiveMalayLearner } from "../../modules/curriculum/learnerCurriculumOverride";

const STUDENT_HOME_HERO = "/images/student-home-hero.png";
const PROGRESS_FILL =
  "bg-gradient-to-r from-indigo-500 via-app-primary/90 to-pink-400";

function moduleLabel(lesson: LessonItem, lessonsFallback: string): string {
  const sk = lesson.section_key;
  if (sk != null && String(sk).trim()) return String(sk).trim();
  const tt = lesson.topic_ta;
  if (tt != null && String(tt).trim()) return String(tt).trim();
  return lessonsFallback;
}

function activityHint(lesson: LessonItem, aiTutorLabel: string): string {
  const kind = (lesson.lesson_type || "").toLowerCase().replace(/[\s-]+/g, "_");
  if (kind === "body_parts") return "உடல் உறுப்புகள்";
  if (kind === "vocabulary" || kind === "vocab") return "சொல்வங்கி";
  if (kind === "reading") return "படிப்போம்";
  if (kind === "rhyme" || kind === "song") return "பாடல்";
  if (kind === "story") return "கதை";
  const first = lesson.sublessons?.[0];
  if (first?.type) return String(first.type);
  return aiTutorLabel;
}

type SubtopicRow = { no: string; title: string; type?: string; pct: number };

function buildSubtopicRows(lesson: LessonItem, prog?: TutorLessonProgressDTO): SubtopicRow[] {
  const api = prog?.sublessons;
  const fromLesson = lesson.sublessons || [];
  if (api?.length) {
    return api.map((s) => ({
      no: s.sublesson_no,
      title: (s.title || s.sublesson_no).trim(),
      type: s.type,
      pct: Math.min(100, Math.max(0, s.percent)),
    }));
  }
  if (fromLesson.length) {
    return fromLesson.map((s) => ({
      no: s.sublesson_no,
      title: (s.title || s.sublesson_no).trim(),
      type: s.type,
      pct: 0,
    }));
  }
  return [];
}

export const TutorLessonProgressPanel: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { labels, loading: labelsLoading } = useTutorTenantLabels();
  const { curriculum } = useLearnerCurriculum();
  const isLearnerMalay = isEffectiveMalayLearner({
    curriculum,
    legacyTenantIsMalay: labels.isMalay,
  });
  const { isSchoolFeatureEnabled } = useFeatureAccess();
  const { grade, isRosterLoading } = useStudentMe();
  const vocabularyEnabled = isSchoolFeatureEnabled("vocabulary");
  const lessonsFallback = t("dashboard.studentHome.lessonsFallback");
  const aiTutorLabel = t("dashboard.studentHome.aiTutor");
  const [lessons, setLessons] = useState<LessonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLessonNo, setExpandedLessonNo] = useState<number | null>(null);

  const progressLessonNumbers = useMemo(
    () => lessons.slice(0, 36).map((l) => l.lesson_no),
    [lessons],
  );

  const {
    progressByLesson,
    isProgressLoading: progressBatchLoading,
    isError: progressBatchError,
  } = useTutorProgressBatch({
    grade,
    lessonNumbers: progressLessonNumbers,
    enabled:
      isSchoolFeatureEnabled("ai_tutor") &&
      !isRosterLoading &&
      grade != null &&
      grade > 0 &&
      progressLessonNumbers.length > 0,
  });

  const loadLessons = useCallback(async (opts?: { bypassCache?: boolean }) => {
    setLoading(true);
    setError(null);
    try {
      const list = await listLessons({ bypassCache: opts?.bypassCache });
      setLessons(list);
    } catch (e: unknown) {
      setError(getApiErrorMessage(e, { emptyLessonsMessage: labels.emptyLessonsMessage }));
      setLessons([]);
    } finally {
      setLoading(false);
    }
  }, [labels.emptyLessonsMessage]);

  useEffect(() => {
    // Malay tenants do not use the Tamil lesson-assignment TOC.
    if (labelsLoading || isLearnerMalay) return;
    if (!isSchoolFeatureEnabled("ai_tutor")) return;
    void loadLessons();
  }, [loadLessons, isSchoolFeatureEnabled, labelsLoading, isLearnerMalay]);

  const sections = useMemo(() => {
    const m = new Map<string, LessonItem[]>();
    for (const l of lessons) {
      const key = moduleLabel(l, lessonsFallback);
      const arr = m.get(key) || [];
      arr.push(l);
      m.set(key, arr);
    }
    return Array.from(m.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([mod, items]) => ({
        mod,
        items: [...items].sort((a, b) => a.lesson_no - b.lesson_no),
      }));
  }, [lessons, lessonsFallback]);

  const primaryLesson = useMemo(() => {
    if (!lessons.length) return null;
    const scored = lessons.map((l) => ({
      l,
      p: progressByLesson[l.lesson_no]?.lesson_percent ?? 0,
      st: progressByLesson[l.lesson_no]?.status ?? (progressByLesson[l.lesson_no]?.lesson_percent ? 'in_progress' : 'not_started'),
    }));
    const inProgress = scored.find((x) => x.st === 'in_progress' || (x.p > 0 && x.p < 100));
    if (inProgress) return inProgress.l;
    const notStarted = scored.find((x) => x.st === 'not_started');
    if (notStarted) return notStarted.l;
    return lessons[0];
  }, [lessons, progressByLesson]);

  const nextLessonHint = useMemo(() => {
    if (!primaryLesson || lessons.length < 2) return null;
    const idx = lessons.findIndex((l) => l.lesson_no === primaryLesson.lesson_no);
    if (idx < 0 || idx >= lessons.length - 1) return null;
    const n = lessons[idx + 1];
    return { mod: moduleLabel(n, lessonsFallback), title: n.lesson_title, no: n.lesson_no };
  }, [lessons, primaryLesson, lessonsFallback]);

  const primaryPct = primaryLesson
    ? Math.min(100, Math.max(0, progressByLesson[primaryLesson.lesson_no]?.lesson_percent ?? 0))
    : 0;

  function resumeUrl(lessonNo: number, sublessonNo: string, chunkIdx: number): string {
    const base = ROUTES.STUDENT_TAMIL_TUTOR;
    const q = new URLSearchParams();
    q.set("lesson", String(lessonNo));
    q.set("sublesson", sublessonNo);
    q.set("chunk", String(chunkIdx));
    return `${base}?${q.toString()}`;
  }

  async function goResumeLesson(lessonNo: number): Promise<void> {
    try {
      const r = await getTutorLessonResume(lessonNo);
      navigate(resumeUrl(r.lesson_no, r.sublesson_no, r.chunk_order_index));
    } catch {
      const lesson = lessons.find((l) => l.lesson_no === lessonNo);
      const first = lesson?.sublessons?.[0]?.sublesson_no;
      if (first) navigate(resumeUrl(lessonNo, first, 0));
    }
  }

  async function goResumeSublesson(lessonNo: number, sublessonNo: string): Promise<void> {
    try {
      const r = await getTutorLessonResume(lessonNo);
      if (r.sublesson_no === sublessonNo) {
        navigate(resumeUrl(r.lesson_no, r.sublesson_no, r.chunk_order_index));
        return;
      }
    } catch {
      /* fall through */
    }
    navigate(resumeUrl(lessonNo, sublessonNo, 0));
  }

  function toggleLesson(lessonNo: number): void {
    setExpandedLessonNo((prev) => (prev === lessonNo ? null : lessonNo));
  }

  if (!isSchoolFeatureEnabled("ai_tutor")) return null;
  if (labelsLoading || isLearnerMalay) return null;

  if (loading || progressBatchLoading) {
    return (
      <div className="mt-6 rounded-2xl border border-app-border dark:border-slate-700 bg-app-surface dark:bg-slate-900/50 p-6 flex items-center gap-2 text-app-text-muted dark:text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin shrink-0 text-app-primary dark:text-app-primary/80" />
        <span className="text-sm">{t("dashboard.studentHome.loadingTutorProgress")}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-6 rounded-2xl border border-warning-200 dark:border-warning-800 bg-warning-50/80 dark:bg-warning-950/30 px-4 py-4 text-sm text-warning-900 dark:text-warning-100 space-y-3">
        <p>{error}</p>
        <button
          type="button"
          onClick={() => void loadLessons({ bypassCache: true })}
          className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-warning-800/10 dark:bg-warning-200/10 px-3 py-1.5 text-xs font-semibold hover:bg-warning-800/15"
        >
          {t("dashboard.studentHome.tryAgain")}
        </button>
      </div>
    );
  }

  if (lessons.length === 0) {
    return (
      <div className="mt-6 rounded-2xl border border-dashed border-purple-200 bg-gradient-to-br from-white via-indigo-50/40 to-violet-50/30 p-6 text-center space-y-3 dark:border-purple-500/35 dark:from-gray-800 dark:via-gray-800/90 dark:to-gray-900/80">
        <p className="text-sm font-medium text-indigo-950 dark:text-white">
          {t("dashboard.studentHome.noLessonsTitle")}
        </p>
        <p className="text-xs text-slate-600 dark:text-white/75 max-w-md mx-auto leading-relaxed">
          {t("dashboard.studentHome.noLessonsBody")}
        </p>
        <button
          type="button"
          onClick={() => void loadLessons({ bypassCache: true })}
          className="inline-flex min-h-[44px] items-center justify-center rounded-xl bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 px-4 py-2 text-sm font-semibold text-white shadow-md hover:brightness-110"
        >
          {t("dashboard.studentHome.tryAgain")}
        </button>
      </div>
    );
  }

  return (
    <section id="continue-learning" className="mt-6 space-y-4">
      {primaryLesson && (
        <div className="relative rounded-2xl overflow-hidden shadow-lg shadow-purple-500/20 border border-white/20">
          <img
            src={STUDENT_HOME_HERO}
            alt=""
            className="block w-full h-auto min-h-[10rem] sm:min-h-[11rem] object-cover object-center"
            aria-hidden
          />
          <div className="absolute inset-0">
            <div className="flex flex-col justify-center h-full px-4 sm:px-6 lg:px-8 py-4 sm:py-5 min-w-0 max-w-[58%] text-app-primary">
              <p className="text-[10px] sm:text-[11px] uppercase tracking-wide text-app-primary font-semibold">
                {t("dashboard.studentHome.todaysLearning")}
              </p>
              <p className="text-[11px] text-app-primary mt-2">{t("dashboard.studentHome.resume")}</p>
              <h3 className="text-lg sm:text-xl font-bold truncate text-app-primary">
                {labels.moduleTitleAiTutor}
              </h3>
              <p className="text-sm text-app-primary truncate">
                <span className="font-medium">{moduleLabel(primaryLesson, lessonsFallback)}</span>
                <span className="mx-1.5 text-app-primary">→</span>
                <span>{primaryLesson.lesson_title}</span>
              </p>
              <div className="space-y-1 pt-2 max-w-md">
                <div className="flex justify-between text-[11px] text-app-primary">
                  <span>{t("dashboard.studentHome.progressShort")}</span>
                  <span className="font-semibold tabular-nums">{primaryPct.toFixed(0)}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-indigo-200/80 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-300 ${PROGRESS_FILL}`}
                    style={{ width: `${primaryPct}%` }}
                  />
                </div>
              </div>
              {nextLessonHint && (
                <p className="text-[11px] text-app-primary pt-1 truncate max-w-md">
                  {t("dashboard.studentHome.nextLesson", {
                    mod: nextLessonHint.mod,
                    title: nextLessonHint.title,
                  })}
                </p>
              )}
              <div className="pt-3">
                <Button
                  type="button"
                  aria-label={t("dashboard.studentHome.resumeAiTutorAria")}
                  className="h-11 sm:h-12 px-4 sm:px-5 rounded-xl !bg-white !text-app-primary hover:!bg-indigo-50 shadow-lg border-0 gap-2 focus:ring-purple-300 font-semibold text-sm"
                  onClick={() => void goResumeLesson(primaryLesson.lesson_no)}
                >
                  <Play className="h-5 w-5" fill="currentColor" />
                  {t("dashboard.studentHome.continueLesson")}
                </Button>
              </div>
            </div>
          </div>
          {vocabularyEnabled && (
            <div className="px-4 py-3 sm:absolute sm:inset-0 sm:flex sm:items-center sm:justify-end sm:pr-[3%] lg:pr-[4%] sm:py-0 sm:px-0 pointer-events-none">
              <div className="sm:w-[24%] sm:max-w-[11.5rem] flex items-center justify-center pointer-events-auto">
                <WordOfDayHeroPanel lessonNo={primaryLesson.lesson_no} variant="banner" />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="rounded-2xl border border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-800 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-app-border dark:border-gray-700 flex items-start justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold text-app-text dark:text-white leading-tight">
              {t("dashboard.studentHome.lessonProgressTitle")}
            </h2>
            <p className="text-xs text-app-text-muted dark:text-gray-400 mt-0.5">
              {t("dashboard.studentHome.lessonProgressHint")}
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadLessons({ bypassCache: true })}
            className="text-xs font-medium text-app-primary dark:text-app-primary/80 hover:underline shrink-0"
          >
            {t("dashboard.studentHome.refresh")}
          </button>
        </div>

        <div
          className="max-h-[min(20rem,45vh)] overflow-y-auto overscroll-contain"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          {sections.map(({ mod, items }) => (
            <div key={mod}>
              <div className="sticky top-0 z-[1] px-4 py-2 bg-app-surface-muted/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-app-border dark:border-gray-700 text-xs font-semibold text-app-text-muted dark:text-gray-300">
                {mod}
              </div>
              <ul className="divide-y divide-app-border dark:divide-gray-700">
                {items.map((lesson) => {
                  const pct = Math.min(
                    100,
                    Math.max(0, progressByLesson[lesson.lesson_no]?.lesson_percent ?? 0)
                  );
                  const prog = progressByLesson[lesson.lesson_no];
                  const subtopics = buildSubtopicRows(lesson, prog);
                  const open = expandedLessonNo === lesson.lesson_no;

                  return (
                    <li key={lesson.lesson_no}>
                      <div>
                        <button
                          type="button"
                          className="w-full text-left px-4 py-3 hover:bg-app-surface-muted/60 dark:hover:bg-slate-900/60 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-app-primary focus-visible:ring-inset"
                          onClick={() => toggleLesson(lesson.lesson_no)}
                          aria-expanded={open}
                        >
                          <div className="flex items-start gap-2">
                            <span className="mt-0.5 text-app-primary dark:text-fuchsia-400 shrink-0">
                              {open ? (
                                <ChevronDown className="h-4 w-4" aria-hidden />
                              ) : (
                                <ChevronRight className="h-4 w-4" aria-hidden />
                              )}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="font-medium text-app-text dark:text-white text-sm leading-snug line-clamp-2">
                                    {lesson.lesson_title}
                                  </p>
                                  <p className="text-xs text-app-primary dark:text-fuchsia-400 mt-0.5">
                                    {activityHint(lesson, aiTutorLabel)}
                                  </p>
                                </div>
                                <div className="flex flex-col items-end shrink-0">
                                  <span className="text-sm font-semibold tabular-nums text-app-text dark:text-gray-200">
                                    {pct.toFixed(0)}%
                                  </span>
                                  {prog?.status && (
                                    <span className="text-[10px] text-app-text-muted dark:text-gray-400 font-medium">
                                      {prog.status === 'completed'
                                        ? 'Completed'
                                        : prog.status === 'in_progress'
                                          ? 'In Progress'
                                          : 'Not Started'}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="mt-2 h-2 rounded-full bg-slate-200 dark:bg-gray-700 overflow-hidden ring-1 ring-inset ring-purple-500/10 dark:ring-purple-400/15">
                                <div
                                  className={`h-full rounded-full transition-all ${PROGRESS_FILL}`}
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </button>

                        {open && (
                          <div className="px-4 pb-3 pl-11 space-y-2 border-t border-app-border/60 dark:border-gray-700/80 bg-app-surface-muted/40 dark:bg-gray-900/40">
                            {subtopics.length === 0 ? (
                              <p className="text-xs text-app-text-muted dark:text-gray-500 py-2">
                                {t("dashboard.studentHome.noSubtopics")}
                              </p>
                            ) : (
                              subtopics.map((sub) => (
                                <div
                                  key={sub.no}
                                  className="rounded-lg border border-app-border dark:border-gray-700 bg-app-surface dark:bg-gray-900/80 p-3"
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-app-text dark:text-white line-clamp-2">
                                        {sub.title}
                                      </p>
                                      {sub.type ? (
                                        <p className="text-[11px] text-app-primary dark:text-fuchsia-400 mt-0.5">
                                          {sub.type}
                                        </p>
                                      ) : null}
                                    </div>
                                    <span className="text-xs font-semibold tabular-nums text-app-text dark:text-gray-200 shrink-0">
                                      {sub.pct.toFixed(0)}%
                                    </span>
                                  </div>
                                  <div className="mt-2 h-1.5 rounded-full bg-slate-200 dark:bg-gray-700 overflow-hidden">
                                    <div
                                      className="h-full rounded-full bg-primary-400 dark:bg-app-primary"
                                      style={{ width: `${sub.pct}%` }}
                                    />
                                  </div>
                                  <button
                                    type="button"
                                    className="mt-2.5 w-full sm:w-auto text-xs font-semibold rounded-lg px-3 py-1.5 text-white bg-gradient-to-r from-app-primary via-app-primary/90 to-app-primary/75 hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-app-primary focus:ring-offset-1 dark:focus:ring-offset-slate-900 shadow-sm shadow-app-primary/20"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void goResumeSublesson(lesson.lesson_no, sub.no);
                                    }}
                                  >
                                    {t("dashboard.studentHome.continue")}
                                  </button>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
        <p className="px-4 py-2 text-[10px] text-app-text-muted dark:text-gray-500 border-t border-app-border dark:border-gray-700">
          {t("dashboard.studentHome.lessonPercentFooter")}
        </p>
      </div>
    </section>
  );
};
